/**
 * Apartmentliste — Bestand, Belegungsstreifen und Kanalzustand auf einen Blick.
 *
 * Filter, Suche und Seite liegen in der URL (§5). Das ist hier nicht nur Komfort: die
 * Vor-/Zurück-Pfeile der Detailansicht (F4) lesen genau diesen Kontext, um zu wissen, welche
 * Nachbarn die Liste gerade zeigt.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button, PageHeader, Pagination } from '@/shared/components';
import { I } from '@/shared/icons';
import { useApi } from '@/shared/api';
import { canManage, isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { useObjectScope } from '@/shared/object-scope';
import { PropertyFilters } from './PropertyFilters';
import { DEFAULT_PROPERTY_FILTERS } from './propertiesListSearch';
import { PropertiesEmptyState } from './properties-list/PropertiesEmptyState';
import { PropertyList } from './properties-list/PropertyList';
import { PropertySearchField } from './properties-list/PropertySearchField';
import { usePropertiesList } from './usePropertiesList';

export function PropertiesPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = canManage(user);
  const { propertyIds } = useObjectScope();
  const list = usePropertiesList();

  const stripFrom = new Date().toISOString().slice(0, 10);
  const stripToDate = new Date();
  stripToDate.setUTCDate(stripToDate.getUTCDate() + 13);
  const stripTo = stripToDate.toISOString().slice(0, 10);
  const { data: calendar } = useQuery({
    queryKey: ['calendar', 'strip', stripFrom, stripTo, propertyIds],
    queryFn: () => api.calendar.getRange(stripFrom, stripTo, propertyIds),
    staleTime: 60_000,
  });
  const cellsByProperty = new Map(
    (calendar?.properties ?? []).map((row) => [row.propertyId, row.cells]),
  );

  const { data: channelsData } = useQuery({
    queryKey: ['channex', 'channels'],
    queryFn: () => api.channex.listChannels(),
    staleTime: 60_000,
    // Channex-Channel-Liste ist WorkspaceAdmin-only (Server: ChannexConnectionsController).
    enabled: isWorkspaceAdmin(user),
  });
  const channels = channelsData?.data ?? [];

  // Der Stadtfilter wirkt nur auf der geladenen Seite; sagt die Zeile darüber „12 Apartments",
  // während acht dastehen, widerspricht die Auskunft dem, was man sieht.
  const localOnly = list.filters.city !== 'all';
  const description = !list.hasData
    ? 'Wird geladen…'
    : localOnly
      ? `${list.items.length} von ${list.pageItems.length} auf dieser Seite · Stadt ${list.filters.city}`
      : `${list.total} Apartment${list.total === 1 ? '' : 's'}${list.filtered ? ' — gefiltert' : ''}`;

  return (
    <>
      <PageHeader
        eyebrow="Inventar"
        title="Ihre"
        titleAccent="Apartments"
        description={description}
        actions={
          canEdit && (
            <Button
              variant="brand"
              leftIcon={<I name="Plus" size={14} />}
              onClick={() => void navigate({ to: '/properties/new' })}
            >
              Neues Apartment
            </Button>
          )
        }
      />

      <div className="mb-4">
        <PropertySearchField
          value={list.filters.query}
          onChange={(query) => list.setFilters({ ...list.filters, query })}
        />
      </div>

      {list.pageItems.length > 0 && (
        <PropertyFilters
          filters={list.filters}
          onChange={list.setFilters}
          properties={list.pageItems}
        />
      )}

      {list.emptyKind ? (
        <PropertiesEmptyState
          kind={list.emptyKind}
          canEdit={canEdit}
          onResetFilters={() => list.setFilters(DEFAULT_PROPERTY_FILTERS)}
          onCreate={() => void navigate({ to: '/properties/new' })}
          onFirstPage={() => list.setPage(1)}
        />
      ) : (
        <>
          <PropertyList
            properties={list.items}
            cellsByProperty={cellsByProperty}
            stripFrom={stripFrom}
            channels={channels}
            listContext={list.listContext as unknown as Record<string, unknown>}
          />

          {/*
            Die Seitenschaltung bleibt auch bei aktivem Stadtfilter stehen. Vorher verschwand sie —
            wer auf Seite 1 nach einer Stadt filterte, kam an die übrigen Seiten seines Bestands
            gar nicht mehr heran.
          */}
          {list.hasData && list.total > list.pageSize && (
            <Pagination
              page={list.page}
              pageSize={list.pageSize}
              total={list.total}
              onPageChange={list.setPage}
            />
          )}
        </>
      )}
    </>
  );
}
